import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, TextField, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Document, Page, pdfjs } from 'react-pdf';
import OtpInput from 'react-otp-input';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { DialogWrapper } from '../../shared/components/common/dialogWrapper';
import { ButtonWrapper } from '../../shared/components/formUtilities/useButton';
import Logo from '../../shared/components/common/logo';
import { SigningService } from '../../services/signingService';
import UaePassSignWithButton from '../../assets/images/uaepass_sign_with_button.svg';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const SESSION_KEY = (id) => `signing_token_${id}`;

// Our API's BadRequest responses serialize ModelState directly as a flat
// { message: [code] } dictionary (BaseController.CustomResult), not wrapped in
// an "errors" key. Framework-level errors (404s, etc.) use ProblemDetails
// instead, with title/errors — handle both shapes.
const extractErrorMessage = (err) => {
    const data = err?.response?.data;
    if (!data || typeof data !== 'object') return undefined;
    if (typeof data.message === 'string') return data.message;
    if (data.errors && typeof data.errors === 'object') {
        const firstMessage = Object.values(data.errors)[0]?.[0];
        if (firstMessage) return firstMessage;
    }
    const firstValue = Object.values(data)[0];
    return (Array.isArray(firstValue) ? firstValue[0] : undefined) || data.title;
};

const STATUS = {
    AUTH_EMAIL: 'auth_email',
    AUTH_OTP: 'auth_otp',
    AUTH_NATIONAL_ID: 'auth_national_id',
    LOADING: 'loading',
    ACTIVE: 'active',
    COMPLETED: 'completed',
    REJECTED: 'rejected',
    ERROR: 'error',
    SIGN_ERROR: 'sign_error',
};

const SIGN_ERROR_COPY = {
    auth_cancelled: {
        body1: 'User cancelled the login.',
    },
    not_eligible: {
        body1: 'You are not eligible to access this service. Your account is either not upgraded or you have a visitor account.',
    },
    not_qualified: {
        body1: 'Your UAE PASS signature is not qualified. Please upgrade your account following instructions in the UAE PASS app.',
    },
    identity_mismatch: {
        body1: 'This Service is only for registered users, please contact Funding Souq in order to access the services.',
    },
};

const UNKNOWN_SIGN_ERROR_COPY = {
    body1: 'Something went wrong while verifying your identity. Please contact Funding Souq support for assistance.',
};

const DarkErrorCard = ({ body1 }) => (
    <Box className="flex flex-col items-center justify-center min-h-screen bg-[#f5f6f8] px-6">
        <Box className="w-full max-w-[520px]">
            <Logo disabledLink sx={{ width: 'auto', height: 56 }} classList="mx-auto !mb-8 flex justify-center" />
            <Box className="bg-[#1b3034] rounded-2xl px-10 py-9 text-left">
                <Typography className="!text-[#a8bfc4] !text-[15px] !leading-[1.7]">
                    {body1}
                </Typography>
            </Box>
            <Typography className="!text-[#9aa5b1] !text-[13px] !text-center !mt-6">
                © Funding Souq Limited · Regulated by the DFSA
            </Typography>
        </Box>
    </Box>
);

const UaePassSignButton = ({ loading, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={loading}
        className="relative shrink-0 -ml-2 disabled:opacity-70 disabled:cursor-not-allowed"
    >
        <img src={UaePassSignWithButton} alt="Sign with UAE PASS" className="h-10 w-auto block" />
        {loading && (
            <Box className="absolute inset-0 flex items-center justify-center">
                <CircularProgress size={20} />
            </Box>
        )}
    </button>
);

export const SignPage = () => {
    const { signerProcessId } = useParams();
    const [searchParams] = useSearchParams();
    const signErrorCode = searchParams.get('error');

    const [status, setStatus] = useState(STATUS.LOADING);
    const [info, setInfo] = useState(null);
    const [maskedEmail, setMaskedEmail] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [nationalId, setNationalId] = useState('');
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState('');
    const [numPages, setNumPages] = useState(null);
    const [pdfWidth, setPdfWidth] = useState(900);
    const [signing, setSigning] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    const serviceRef = useRef(null);

    useEffect(() => {
        const updateWidth = () => setPdfWidth(Math.min(window.innerWidth - 64, 900));
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    const loadSigningInfo = useCallback(async (token) => {
        serviceRef.current = new SigningService(token);
        setStatus(STATUS.LOADING);

        try {
            // Identity comes before document review: if UAE Pass hasn't verified this
            // signatory yet, send them there immediately rather than showing the contract.
            const initResp = await serviceRef.current.initiateSign(signerProcessId);
            if (initResp.data?.authUrl) {
                window.location.href = initResp.data.authUrl;
                return;
            }
            if (initResp.data?.requiresNationalId) {
                setStatus(STATUS.AUTH_NATIONAL_ID);
                return;
            }
        } catch (err) {
            if (err?.response?.status === 401) {
                sessionStorage.removeItem(SESSION_KEY(signerProcessId));
                setStatus(STATUS.AUTH_EMAIL);
                return;
            }
            // Not eligible to initiate (e.g. already Completed/Rejected) — fall through
            // to GetSigningInfo below to render the correct terminal state.
        }

        try {
            const resp = await serviceRef.current.getSigningInfo(signerProcessId);
            const data = resp.data;
            setInfo(data);
            if (data.status === 'Created') {
                setStatus(STATUS.ACTIVE);
            } else if (data.status === 'Completed') {
                setStatus(STATUS.COMPLETED);
            } else {
                setStatus(STATUS.REJECTED);
            }
        } catch (err) {
            if (err?.response?.status === 401) {
                sessionStorage.removeItem(SESSION_KEY(signerProcessId));
                setStatus(STATUS.AUTH_EMAIL);
            } else {
                setStatus(STATUS.ERROR);
            }
        }
    }, [signerProcessId]);

    useEffect(() => {
        if (signErrorCode) {
            setStatus(STATUS.SIGN_ERROR);
            return;
        }

        let cancelled = false;
        const stored = sessionStorage.getItem(SESSION_KEY(signerProcessId));
        if (stored) {
            loadSigningInfo(stored);
        } else {
            new SigningService(null).getEmailHint(signerProcessId)
                .then((resp) => { if (!cancelled) { setMaskedEmail(resp.data?.maskedEmail ?? ''); } })
                .catch(() => { if (!cancelled) { setMaskedEmail('your registered email'); } })
                .finally(() => { if (!cancelled) { setStatus(STATUS.AUTH_EMAIL); } });
        }
        return () => { cancelled = true; };
    }, [signerProcessId, signErrorCode, loadSigningInfo]);

    const handleRequestOtp = async () => {
        setAuthError('');
        setAuthLoading(true);
        try {
            await new SigningService(null).requestOtp(signerProcessId);
            setStatus(STATUS.AUTH_OTP);
        } catch (err) {
            setAuthError(extractErrorMessage(err) || 'Could not send code. Please try again.');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!otpCode.trim()) return;
        setAuthError('');
        setAuthLoading(true);
        try {
            const resp = await new SigningService(null).verifyOtp(signerProcessId, otpCode.trim());
            const token = resp.data?.token;
            if (token) {
                sessionStorage.setItem(SESSION_KEY(signerProcessId), token);
                loadSigningInfo(token);
            } else {
                setAuthError('Verification failed. Please try again.');
            }
        } catch (err) {
            setAuthError(extractErrorMessage(err) || 'Invalid or expired code. Please try again.');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleSubmitNationalId = async () => {
        if (!nationalId.trim()) return;
        setAuthError('');
        setAuthLoading(true);
        try {
            await serviceRef.current.submitNationalId(signerProcessId, nationalId.trim());
            const token = sessionStorage.getItem(SESSION_KEY(signerProcessId));
            loadSigningInfo(token);
        } catch (err) {
            setAuthError(extractErrorMessage(err) || 'Could not save Emirates ID. Please check the number and try again.');
        } finally {
            setAuthLoading(false);
        }
    };

    const onDocumentLoadSuccess = useCallback(({ numPages: n }) => {
        setNumPages(n);
    }, []);

    const handleSign = async () => {
        setSigning(true);
        try {
            const resp = await serviceRef.current.initiateSign(signerProcessId);
            const url = resp.data?.pendingSignUrl || resp.data?.authUrl;
            if (url) {
                window.location.href = url;
            } else {
                setSigning(false);
            }
        } catch {
            setSigning(false);
        }
    };

    const handleCancelConfirm = async () => {
        setCancelling(true);
        try {
            await serviceRef.current.cancelSigning(signerProcessId);
            setCancelDialogOpen(false);
            setStatus(STATUS.REJECTED);
        } catch {
            setCancelling(false);
        }
    };

    if (status === STATUS.AUTH_EMAIL) {
        return (
            <Box className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4">
                <Box className="w-full max-w-lg bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col items-center text-center overflow-hidden">
                    <Box className="w-full px-8 py-3 border-b border-gray-100 flex justify-center">
                        <Logo sx={{ width: 210, height: 'auto' }} classList="mx-auto" />
                    </Box>
                    <Box className="w-full px-8 py-8 flex flex-col items-center gap-4">
                        <Box className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                            <LockOutlinedIcon className="!text-primary" fontSize="medium" />
                        </Box>
                        <Typography variant="h6" className="!font-semibold">
                            Verify your identity
                        </Typography>
                        <Typography variant="body2" className="!text-gray-500 !-mt-2">
                            We'll send a verification code to
                        </Typography>
                        <Box className="flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2">
                            <MailOutlineIcon fontSize="small" className="!text-gray-500" />
                            <Typography className="!font-semibold">{maskedEmail}</Typography>
                        </Box>
                        {authError && (
                            <Typography variant="body2" className="!text-red-500">
                                {authError}
                            </Typography>
                        )}
                        <ButtonWrapper
                            label="Send verification code"
                            loading={authLoading}
                            onClick={handleRequestOtp}
                            fullWidth
                        />
                    </Box>
                </Box>
            </Box>
        );
    }

    if (status === STATUS.AUTH_OTP) {
        return (
            <Box className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4">
                <Box className="w-full max-w-lg bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col items-center text-center overflow-hidden">
                    <Box className="w-full px-8 py-3 border-b border-gray-100 flex justify-center">
                        <Logo sx={{ width: 210, height: 'auto' }} classList="mx-auto" />
                    </Box>
                    <Box className="w-full px-8 pt-6 pb-2 flex flex-col items-center gap-2">
                        <Box className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                            <MarkEmailReadOutlinedIcon className="!text-primary" fontSize="medium" />
                        </Box>
                        <Typography variant="h6" className="!font-semibold">
                            Enter verification code
                        </Typography>
                        <Typography variant="body2" className="!text-gray-500 !-mt-2">
                            We sent a 6-digit code to
                        </Typography>
                        <Box className="flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2">
                            <MailOutlineIcon fontSize="small" className="!text-gray-500" />
                            <Typography className="!font-semibold">{maskedEmail}</Typography>
                        </Box>
                        <Box className="flex items-center gap-1 !text-gray-400">
                            <AccessTimeIcon fontSize="small" />
                            <Typography variant="caption">It expires in 10 minutes.</Typography>
                        </Box>
                        <OtpInput
                            value={otpCode}
                            onChange={setOtpCode}
                            numInputs={6}
                            inputType="tel"
                            shouldAutoFocus
                            containerStyle="vp-otp otp-container sign-otp-compact"
                            renderInput={(props) => (
                                <input
                                    {...props}
                                    autoComplete="one-time-code"
                                    autoCapitalize="off"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    onKeyDown={(e) => {
                                        props.onKeyDown?.(e);
                                        if (e.key === 'Enter') handleVerifyOtp();
                                    }}
                                    className={`vp-otp-input ${props.value ? 'filled' : ''}`}
                                />
                            )}
                        />
                        {authError && (
                            <Typography variant="body2" className="!text-red-500">
                                {authError}
                            </Typography>
                        )}
                        <ButtonWrapper
                            label="Verify"
                            loading={authLoading}
                            onClick={handleVerifyOtp}
                            disabled={otpCode.length !== 6}
                            fullWidth
                        />
                        <ButtonWrapper
                            variant="text"
                            label="Resend code"
                            onClick={() => { setStatus(STATUS.AUTH_EMAIL); setAuthError(''); setOtpCode(''); }}
                            classList="!text-gray-500 !min-h-0 !py-0 !-mt-1"
                        />
                    </Box>
                </Box>
            </Box>
        );
    }

    if (status === STATUS.AUTH_NATIONAL_ID) {
        return (
            <Box className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4">
                <Box className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-200 p-8 flex flex-col items-center gap-5 text-center">
                    <Logo />
                    <Box className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                        <BadgeOutlinedIcon className="!text-primary" fontSize="medium" />
                    </Box>
                    <Box>
                        <Typography variant="h6" className="!font-semibold">
                            Confirm your Emirates ID
                        </Typography>
                        <Typography variant="body2" className="!text-gray-500 mt-1">
                            We don't have an Emirates ID on file for you yet. Enter it below — we'll confirm it against your UAE PASS account on the next step.
                        </Typography>
                    </Box>
                    <TextField
                        label="Emirates ID number"
                        placeholder="784-XXXX-XXXXXXX-X"
                        fullWidth
                        value={nationalId}
                        onChange={(e) => setNationalId(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSubmitNationalId()}
                        error={!!authError}
                        helperText={authError}
                        size="small"
                    />
                    <ButtonWrapper
                        label="Continue"
                        loading={authLoading}
                        onClick={handleSubmitNationalId}
                        disabled={!nationalId.trim()}
                        fullWidth
                    />
                </Box>
            </Box>
        );
    }

    if (status === STATUS.LOADING) {
        return (
            <Box className="flex items-center justify-center min-h-[60vh]">
                <CircularProgress />
            </Box>
        );
    }

    if (status === STATUS.SIGN_ERROR) {
        const copy = SIGN_ERROR_COPY[signErrorCode] || UNKNOWN_SIGN_ERROR_COPY;
        return <DarkErrorCard body1={copy.body1} />;
    }

    if (status === STATUS.ERROR) {
        return (
            <DarkErrorCard body1="This signing link is invalid or has expired. Please contact Funding Souq support if you need assistance." />
        );
    }

    if (status === STATUS.COMPLETED) {
        return (
            <Box className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4">
                <Box className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-200 p-8 flex flex-col items-center gap-3 text-center">
                    <Box className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mb-2">
                        <Typography variant="h4" className="!text-white">✓</Typography>
                    </Box>
                    <Typography variant="h5" className="!font-semibold">
                        Document signed
                    </Typography>
                    <Typography className="!text-gray-500">
                        You have already signed this document. You will receive a copy by email once all parties have completed signing.
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (status === STATUS.REJECTED) {
        return (
            <DarkErrorCard body1="User cancelled the signing process." />
        );
    }

    return (
        <Box className="flex flex-col min-h-screen bg-gray-50">
            <Box className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
                <Box className="max-w-[960px] mx-auto flex items-center justify-between gap-4">
                    <Box>
                        <Typography variant="subtitle1" className="!font-semibold leading-tight">
                            Finance Contract – Application #{info?.applicationId}
                        </Typography>
                        <Typography variant="body2" className="!text-gray-500">
                            {info?.businessName} · Please review the document below before signing
                        </Typography>
                    </Box>
                    <Box className="flex items-center gap-0 shrink-0">
                        <ButtonWrapper
                            variant="outlined"
                            label="Cancel"
                            classList="!text-red-500 !border-red-300 hover:!bg-red-50"
                            onClick={() => setCancelDialogOpen(true)}
                        />
                        <UaePassSignButton loading={signing} onClick={handleSign} />
                    </Box>
                </Box>
            </Box>

            <Box className="flex-1 flex justify-center py-8 px-4 overflow-auto">
                {info?.documentUrl ? (
                    <Box>
                        <Document
                            file={info.documentUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            loading={
                                <Box className="flex justify-center py-16">
                                    <CircularProgress />
                                </Box>
                            }
                            error={
                                <Box className="flex justify-center py-16">
                                    <Typography className="!text-gray-400">
                                        Could not load document preview.
                                    </Typography>
                                </Box>
                            }
                        >
                            {numPages &&
                                Array.from({ length: numPages }, (_, i) => (
                                    <Box key={i + 1} className="mb-4 shadow-md">
                                        <Page
                                            pageNumber={i + 1}
                                            width={pdfWidth}
                                            renderTextLayer
                                            renderAnnotationLayer
                                        />
                                    </Box>
                                ))}
                        </Document>
                    </Box>
                ) : (
                    <Box className="flex items-center justify-center h-64">
                        <Typography className="!text-gray-400">Document not available</Typography>
                    </Box>
                )}
            </Box>

            <DialogWrapper
                state={cancelDialogOpen}
                onClose={() => setCancelDialogOpen(false)}
                title="Cancel signing"
                maxWidth="sm"
                content={
                    <Box className="flex flex-col gap-3">
                        <Typography>
                            Are you ready to cancel this signing request?
                        </Typography>
                        <Typography className="!text-gray-500">
                            If you are not ready to sign right now, simply close this dialog — you can come back later using the same link from your email or SMS.
                        </Typography>
                        <Typography className="!text-red-500 !text-sm">
                            <strong>Cancel permanently</strong> will reject the entire signing process and cannot be undone.
                        </Typography>
                    </Box>
                }
                actions={
                    <>
                        <ButtonWrapper
                            variant="outlined"
                            label="Come back later"
                            onClick={() => setCancelDialogOpen(false)}
                        />
                        <ButtonWrapper
                            label="Cancel permanently"
                            loading={cancelling}
                            onClick={handleCancelConfirm}
                            classList="!bg-red-500 hover:!bg-red-600"
                        />
                    </>
                }
            />
        </Box>
    );
};
